import * as Sentry from '@sentry/vue';
import { joinURL } from 'ufo';
import { useRouter } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';

// NOTE :: Signatures of a stale chunk after a deploy — the browser holds an old index.html
// and requests a hashed asset that no longer exists. Covers the Chrome/Vite, Safari, and
// "server returned index.html (text/html) for a missing .js" variants.
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /ChunkLoadError/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
  /Unable to preload CSS/,
  /'text\/html' is not a valid JavaScript MIME type/,
];

// One-shot reload guard — a stale deploy must trigger at most one reload, never a loop.
// Coordinates with the pre-boot guard in index.html via `af_chunk_reload_attempts`: any successful
// boot clears it here, which re-arms the inline script's retry budget. The other keys are local.
const LAST_BOOT_VERSION_KEY = 'af_last_boot_version';
const RELOAD_ATTEMPTS_KEY = 'af_chunk_reload_attempts';
const RELOAD_GUARD_KEY = 'af_chunk_reload_at';
const RELOAD_GUARD_WINDOW = 1000 * 10; // 10s
const REFETCH_TIMEOUT = 1000 * 8; // a stalled refetch must never block the reload that follows it

// sessionStorage can THROW just on access (private mode, blocked site data). This runs during app
// boot, so an uncaught throw here would break the whole app — worse than the bug we're fixing.
// These wrappers degrade gracefully: on failure the guard simply loses its memory, the app still loads.
function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

// Returns whether the write stuck. Callers use false to avoid an unbounded reload loop: with storage
// blocked the one-shot guard has no cross-reload memory, so reloading anyway could loop forever.
function safeSessionSet(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);

    return true;
  } catch {
    return false; // storage blocked — caller must not rely on the guard persisting
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // storage blocked — nothing to clear
  }
}

// True while the 10s one-shot window from the last repair reload is still open — the next reload
// request will be suppressed, and the document stays where it is.
function isReloadGuardActive(): boolean {
  const lastReloadAt = Number(safeSessionGet(RELOAD_GUARD_KEY)) || 0;

  return Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW;
}

interface RefetchResult {
  status: number | null; // null → network failure or timeout
  url: string;
}

// One Sentry event per repair, sent BEFORE the reload (the transport uses `keepalive`, so it
// survives the navigation). This is the only place the incident is measurable: the raw error
// is grouped by message and carries no URL list, and the pin cookie is scoped to `path=/assets`,
// which makes it invisible to `document.cookie` on any page — so the refetch statuses stand in
// for it. A 200 on a URL the browser just reported as failed means the server has the file and
// the failure was local (a cached 404, or a pin that routed to an older deploy); a 404 after the
// unpin means the deploy that served this HTML truly lacks the file.
function reportChunkRepair(
  error: unknown,
  to: RouteLocationNormalized,
  failedAssetUrl: string | null,
  results: RefetchResult[]
): void {
  const errorMessage = error instanceof Error ? error.message : String(error ?? '');
  const REPAIR_CONTEXT = {
    app_version: window.APP_VERSION,
    deployment_id: __AF_DEPLOYMENT_ID__,
    error_message: errorMessage,
    failed_asset_url: failedAssetUrl,
    refetch_results: results,
    reload_will_be_suppressed: isReloadGuardActive(),
    // The route PATTERN, never the resolved path: `to.fullPath` substitutes params, and the public
    // `/password/reset/:token` route is lazy-loaded like every other — a chunk failure there would
    // write the live reset token (a credential) into Sentry. No `fullPath` fallback for the same
    // reason. The token is also scrubbed from request.url/breadcrumbs in @/plugins/sentry.ts.
    // Index access, not `.at(-1)`: that method is ES2022 and the build targets Safari 14 (Vite's
    // default), where it is missing — the throw here would skip the reload and strand the user.
    route: to.matched[to.matched.length - 1]?.path ?? String(to.name ?? '(unknown)'),
    visibility: document.visibilityState,
  };

  Sentry.captureMessage('Chunk load failed — repairing cached assets', {
    level: 'warning',
    tags: {
      type: 'chunk-repair',
      online: String(navigator.onLine),
      section: 'useChunkPreloadErrorHandling',
    },
    extra: REPAIR_CONTEXT,
  });
}

// Drops the Vercel skew pin (`__vdpl`, see index.html / @Libraries skewProtection). Duplicated here
// on purpose: importing @Libraries from a boot-time composable risks an import cycle.
function unpinAssetsFromDeployment(): void {
  document.cookie = '__vdpl=; path=/assets; max-age=0; secure; samesite=lax';
}

// Always settles within REFETCH_TIMEOUT. A fetch that hangs instead of failing would otherwise
// leave the document with the repair flag set and no reload ever scheduled — stranding the user in
// exactly the way this file exists to prevent. The abandoned request dies with the document.
function withTimeout<T>(promise: Promise<T>, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), REFETCH_TIMEOUT);

    promise
      .then((value) => resolve(value))
      .catch(() => resolve(fallback))
      .finally(() => clearTimeout(timer));
  });
}

// The failing asset URL rides inside the error message, e.g.
// "Failed to fetch dynamically imported module: https://host/assets/People.view-abc123.js".
// That URL names the module the router asked for — NOT necessarily the file that failed. Chrome
// reports a broken static import (a dependency of the route chunk) under the importer's URL, so
// the real 404 can sit one level down. Matches an absolute or a root-relative /assets/ URL;
// fetch() accepts either.
// Assumes assets live at the ROOT /assets/ path — true today, because vite.config.ts sets no
// `base`. If a non-root `base` is ever set, a schemeless message like "Unable to preload CSS for
// /app/assets/x.css" would match as "/assets/x.css" and the repair would silently fetch the wrong
// path — make this base-aware first (joinURL with import.meta.env.BASE_URL, the way
// reloadAppAtPath already does).
function getFailedAssetUrl(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const match = message.match(/(?:https?:\/\/[^\s'")]+)?\/assets\/[^\s'")]+/);

  return match ? match[0] : null;
}

// Every hashed dependency Vite queued so far. Vite's preload helper appends one
// `<link rel="modulepreload">` per JS dependency of a lazy chunk and does NOT wait on it, so a
// dependency that 404s never surfaces by name — only through the importer's error above. Vercel
// answers a missing /assets/ file with a 404 that carries the same one-year `immutable` header
// as a real file, so a single transient miss (a stale skew pin, an edge blip) is stored for a
// year and re-read on every plain reload; only a `cache: 'reload'` fetch of THAT URL overwrites
// it. Real incident: two login-screen dependencies stuck at 404 while the route chunk itself was
// fine — the repair refetched the route chunk, reloaded, and hit the same cached 404s.
function getModulePreloadUrls(): string[] {
  const links = document.querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"]');

  return Array.from(links)
    .map((link) => link.href)
    .filter((href) => href.includes('/assets/'));
}

// The entry's static imports, snapshotted at module evaluation — before the router's initial
// navigation (a microtask) can append any lazy-route dependency link. The document booted, so
// every one of these loaded; refetching them can never repair anything, and on production they
// weigh ~1.3 MB over the wire (14 vendor bundles). Only runtime-appended preloads can hold a
// cached 404, so the repair set below excludes these.
const BOOT_PRELOAD_URLS = new Set(getModulePreloadUrls());

// link.href is always absolute; the URL inside an error message is often root-relative. Normalize
// so the two forms of the same file deduplicate instead of being fetched twice.
function toAbsoluteUrl(url: string): string {
  return new URL(url, globalThis.location.origin).href;
}

// The failing URL (when known) plus every RUNTIME-appended dependency (boot links excluded — see
// BOOT_PRELOAD_URLS), deduplicated. May be empty when the error carried no URL and nothing
// repairable was preloaded — the caller then falls back to a plain reload.
function getAssetUrlsToRepair(failedAssetUrl: string | null): string[] {
  const runtimeUrls = getModulePreloadUrls().filter((url) => !BOOT_PRELOAD_URLS.has(url));
  const failedUrls = failedAssetUrl ? [failedAssetUrl] : [];

  return Array.from(new Set([...failedUrls, ...runtimeUrls].map(toAbsoluteUrl)));
}

export default function (): void {
  const router = useRouter();
  const chunkErrors = new Set<unknown>();

  // Reaching this line means the app booted, so whatever incident spent the pre-boot retry budget
  // is over — give it back on EVERY boot, not only on a version change. Otherwise a same-version
  // recovery (deploy still settling, transient 404) leaves the count raised, and the next incident
  // in this tab skips the first-retry sibling sweep and starts closer to the last-resort notice.
  // Safe: the count is written before each reload and only cleared here, which a broken build
  // never reaches, so the 8-attempt cap still holds per incident.
  safeSessionRemove(RELOAD_ATTEMPTS_KEY);

  // The 10s one-shot reload guard is different: it must survive a same-version re-boot, or the
  // stale version could reload itself in a loop. Only a NEW version releases it.
  const isNewVersionBoot = safeSessionGet(LAST_BOOT_VERSION_KEY) !== window.APP_VERSION;

  if (isNewVersionBoot) {
    safeSessionSet(LAST_BOOT_VERSION_KEY, window.APP_VERSION);
    safeSessionRemove(RELOAD_GUARD_KEY);
  }

  const CHECK_INTERVAL = 1000 * 60 * 1; // 1 minute
  let backgroundVersionIntervalStarted = false;
  let hasPendingUpdate = false;
  let isRepairing = false;

  // Returns whether it navigated — false means the one-shot guard suppressed the reload,
  // so the caller must keep the app usable on the old version (e.g. still call next()).
  function reloadAppAtPath(to: RouteLocationNormalized): boolean {
    if (isReloadGuardActive()) return false;

    // Can't persist the guard (storage blocked) → a reload would have no memory and could loop.
    // Refuse; returning false keeps the app on the old version (the caller still calls next()).
    const guardPersisted = safeSessionSet(RELOAD_GUARD_KEY, String(Date.now()));

    if (!guardPersisted) return false;

    const path = joinURL(import.meta.env.BASE_URL, to.fullPath);

    if (globalThis.location.pathname !== path) {
      globalThis.location.href = path;
      return true;
    }

    globalThis.location.reload();
    return true;
  }

  // A reload alone can NEVER clear a wrong cached copy: the browser does not revalidate an
  // `immutable` sub-resource on reload, so it re-reads the same bad bytes every time. This is the
  // same rescue the pre-boot script in index.html performs, applied to lazy route chunks — without
  // it they recover through a plain reload that cannot work.
  // The reload afterwards is NOT optional: a failed dynamic import stays failed in this document's
  // module map even once the cached copy is repaired, so only a fresh document actually loads it.
  // `urls` is the failing module plus every modulepreload dependency (getAssetUrlsToRepair): the
  // cached 404 is usually on a dependency, and refetching only the importer leaves it in place.
  function repairAssetThenReload(
    error: unknown,
    failedAssetUrl: string | null,
    urls: string[],
    to: RouteLocationNormalized
  ): void {
    if (isRepairing) return;

    isRepairing = true;

    // A missing chunk makes the skew pin the first suspect; drop it so the refetch and the reload
    // that follows talk to the deploy that serves the HTML. The next boot re-pins (index.html).
    unpinAssetsFromDeployment();

    // `mode`/`credentials` mirror how the browser fetches a `crossorigin` module script, so the
    // cache entry overwritten is the same one the next module load reads. Each request is
    // time-boxed on its own; one stalled file must not hold the reload for the others.
    const refetches = urls.map((url) =>
      withTimeout<RefetchResult>(
        fetch(url, { cache: 'reload', mode: 'cors', credentials: 'same-origin' }).then((response) => ({
          status: response.status,
          url,
        })),
        { status: null, url }
      )
    );

    // `then`, not `finally`: every refetch settles through withTimeout, so this never rejects and
    // the results are needed for the report.
    Promise.all(refetches).then((results) => {
      reportChunkRepair(error, to, failedAssetUrl, results);

      const didReload = reloadAppAtPath(to);

      // Reload suppressed (the 10s guard, or storage blocked) → this document is staying put, so
      // release the flag or every later chunk error would early-return and never recover. On a
      // real reload the flag stays set on purpose: a burst of errors must not start a second
      // repair while the navigation is in flight. The `pageshow` listener below covers the one
      // way that document could come back with the flag still set.
      if (!didReload) isRepairing = false;
    });
  }

  async function checkVersionMatch(): Promise<boolean> {
    try {
      const res = await fetch('/meta.json', { cache: 'no-store' });
      if (!res.ok) return true;

      const { version } = (await res.json()) as { version: string };

      return version === window.APP_VERSION;
    } catch {
      return true; // allow navigation if check fails
    }
  }

  function startBackgroundVersionCheck(): void {
    if (backgroundVersionIntervalStarted) return;
    backgroundVersionIntervalStarted = true;

    setInterval(async () => {
      const isMatch = await checkVersionMatch();
      if (!isMatch) {
        console.info('[version-check] New version detected — will reload on next navigation.');
        hasPendingUpdate = true;
      }
    }, CHECK_INTERVAL);
  }

  router.beforeEach((to, from, next) => {
    chunkErrors.clear();

    if (hasPendingUpdate) {
      const didReload = reloadAppAtPath(to);

      if (didReload) {
        console.info('[version-check] Reloading with new version...');
        return;
      }
    }

    startBackgroundVersionCheck();
    next();
  });

  // A repair navigation deliberately leaves `isRepairing` set. Today the document never returns:
  // the HTML is served `no-store`, which makes it ineligible for the back/forward cache (verified —
  // going back builds a fresh document). If those headers ever change, a restored document would
  // come back with the flag set and silently refuse to repair, dead-ending the route with no toast
  // by design. Release it on restore rather than depend on a cache header staying put.
  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted) isRepairing = false;
  });

  // Vite dispatches a plain Event carrying `payload` — NOT a CustomEvent with `detail`.
  // Reading `.detail` stored `undefined`, so `chunkErrors.has(error)` below never matched.
  window.addEventListener('vite:preloadError', (event: Event) => {
    const { payload } = event as Event & { payload?: unknown };

    chunkErrors.add(payload);
  });

  router.onError((error, to) => {
    const isChunkError = error instanceof Error && CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
    const shouldRecover = isChunkError || chunkErrors.has(error) || hasPendingUpdate;

    if (!shouldRecover) return;

    const failedAssetUrl = getFailedAssetUrl(error);
    const urlsToRepair = getAssetUrlsToRepair(failedAssetUrl);

    // Nothing to overwrite in the cache → a plain reload is all that is left to try.
    if (urlsToRepair.length === 0) {
      reportChunkRepair(error, to, failedAssetUrl, []);
      reloadAppAtPath(to);
      return;
    }

    repairAssetThenReload(error, failedAssetUrl, urlsToRepair, to);
  });
}
